/* Next */
import { GetStaticProps } from 'next'
import dynamic from 'next/dynamic'

/* Template fallback */
const LegacyHome = dynamic(() => import('template/pages/Home/Home'))

/* Components */
import LandingPage from 'src/pageBuilder/LandingPage'
import HomeSeo from 'src/pages/Home/Seo'
import { HomepageProductTracking } from 'src/context/RecommendationTrackingContext'
import shopInfos from 'template/infos'

/* Types */
import { MenuPageBuilder, isMenuPageBuilderActive } from 'src/shared/dbTypes/menuPageBuilder'
import { LandingPageProductInquirySettings } from 'src/shared/dbTypes/landingPageSettings'
import { isFilterProductsResponse } from 'src/shared/frontendTypes/productsFilter'
import { tProductPreview } from 'src/shared/frontendTypes/product'
import type { LandingPageImageAiGenerated } from 'src/functions/landingPageImageAiGenerated'

/* Functions */
import { post } from 'src/functions/fetch'
import {
  extractProductInquirySettings,
  extractSitePageBuilder,
} from 'src/pageBuilder/sitePageBuilder'
import { hasPageProductSelection } from 'src/pageBuilder/productBlockSelection'

/* Options */
import config from 'src/config'

interface Props {
  title?: string
  description?: string
  metaTitle?: string
  metaDescription?: string
  metaKeywords?: string
  pageBuilder?: MenuPageBuilder
  sitePageBuilder?: MenuPageBuilder
  productInquiry?: LandingPageProductInquirySettings
  products?: tProductPreview[]
  productPreviewsByKey?: Record<string, tProductPreview>
  count?: number
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

const collectProductKeys = (pageBuilder?: MenuPageBuilder) => {
  const keys = new Set<string>()

  for (const block of pageBuilder?.blocks || []) {
    if (block.type !== 'products') continue

    for (const key of block.productKeys || []) {
      const trimmed = key.trim()
      if (trimmed) keys.add(trimmed)
    }
  }

  return [...keys]
}

const getProductPreviewsByKey = async (
  pageBuilder?: MenuPageBuilder
): Promise<Record<string, tProductPreview> | undefined> => {
  const keys = collectProductKeys(pageBuilder)
  if (keys.length === 0) return undefined

  const data = await post({
    url: 'products',
    body: {
      ...config.shop,
      size: keys.length,
      keys,
    },
    local: true,
  })

  if (data === null || data === undefined)
    throw new Error('Homepage product previews could not be loaded; retain the last valid cache.')
  if (!isFilterProductsResponse(data)) return undefined

  const previewsByKey: Record<string, tProductPreview> = {}
  for (const product of data.filterProducts.products || [])
    previewsByKey[product.key] = product

  return Object.keys(previewsByKey).length > 0 ? previewsByKey : undefined
}

const HomePage: React.FC<Props> = props => {
  const pageBuilder = isMenuPageBuilderActive(props.pageBuilder)
    ? props.pageBuilder
    : props.sitePageBuilder
  const homeBuilder = isMenuPageBuilderActive(pageBuilder) ? pageBuilder : undefined

  const title = props.metaTitle || props.title || homeBuilder?.hero?.title || shopInfos.title
  const description = props.metaDescription || props.description || homeBuilder?.hero?.body || shopInfos.description
  const image = homeBuilder?.hero?.imageSrc || homeBuilder?.hero?.mobileImageSrc
  const imageAlt = homeBuilder?.hero?.imageAlt || homeBuilder?.hero?.mobileImageAlt || homeBuilder?.hero?.title

  if (!isMenuPageBuilderActive(pageBuilder)) return <HomepageProductTracking>
    <LegacyHome />
    <HomeSeo title={title} description={description} />
  </HomepageProductTracking>

  return <HomepageProductTracking>
    <HomeSeo title={title} description={description} image={image} imageAlt={imageAlt} renderMetadata={false} />
    <LandingPage
      {...{
        ...props,
        metaTitle: title,
        metaDescription: description,
        pageBuilder,
        canonicalUrl: '/',
      }}
    />
  </HomepageProductTracking>
}

export const getStaticProps: GetStaticProps<Props> = async () => {
  const data = await post({
    url: 'products',
    body: {
      ...config.shop,
      homePage: true,
    },
    local: true,
  })

  // A transport outage is not an empty CMS configuration. Let ISR retain its
  // last successful page instead of caching the template fallback for a minute.
  if (data === null || data === undefined)
    throw new Error('Homepage data could not be loaded; retain the last valid cache.')
  if (!isFilterProductsResponse(data)) return { props: {}, revalidate: 60 }

  const sitePageBuilder = extractSitePageBuilder(data)
  const productInquiry = extractProductInquirySettings(data)
  const activePageBuilder = isMenuPageBuilderActive(data.menu?.pageBuilder)
    ? data.menu?.pageBuilder
    : sitePageBuilder
  const productPreviewsByKey = await getProductPreviewsByKey(activePageBuilder)
  // Curated rows use their own keyed previews. The unrelated default catalogue
  // is only needed by page selections or the automatic catalogue fallback.
  const includePageProducts = !isMenuPageBuilderActive(activePageBuilder)
    || hasPageProductSelection(activePageBuilder.blocks || [])

  return {
    props: {
      ...(data.menu?.titel ? { title: data.menu.titel } : {}),
      ...(data.menu?.description ? { description: data.menu.description } : {}),
      ...(data.menu?.metaTitle ? { metaTitle: data.menu.metaTitle } : {}),
      ...(data.menu?.metaDescription ? { metaDescription: data.menu.metaDescription } : {}),
      ...(data.menu?.metaKeywords ? { metaKeywords: data.menu.metaKeywords } : {}),
      ...(data.menu?.pageBuilder ? { pageBuilder: data.menu.pageBuilder } : {}),
      ...(sitePageBuilder ? { sitePageBuilder } : {}),
      ...(productInquiry ? { productInquiry } : {}),
      ...(data.landingPageImageAiGenerated
        ? { landingPageImageAiGenerated: data.landingPageImageAiGenerated }
        : {}),
      ...(productPreviewsByKey ? { productPreviewsByKey } : {}),
      count: data.filterProducts.count || 0,
      ...(includePageProducts ? { products: data.filterProducts.products || [] } : {}),
    },
    revalidate: 60,
  }
}

export default HomePage
